import { IconButton, PropRow } from '../../ui/controls.js';
import { materialNames, type ShapeEditorProps } from './doc-context.js';
import { arr, Bool, bool, Color, Enum, IntList, Num, num, obj, OptionalSection, patch, Pick, Section, str, vec, type Obj } from './fields.js';

export const TREE_PRESETS = ['oak', 'pine', 'birch', 'palm', 'willow', 'bush', 'fern', 'flower', 'grassClump', 'deadTree', 'sapling'] as const;
export const CROWN_SHAPES = ['conical', 'spherical', 'columnar', 'umbrella', 'weeping'] as const;
export const LEAF_TYPES = ['broad', 'needle', 'frond', 'blade', 'petal', 'cluster', 'none'] as const;

const PRESET_HINT =
  'Full parameter set; every unset field comes from it. oak: broad rounded crown · pine: conifer whorls · birch: slender, drooping · palm: arching fronds · willow: weeping curtains · bush: multi-stem shrub · fern · flower · grassClump · deadTree: bare · sapling.';

/** `branches`: one count for every level or one per level (`BranchCounts`). */
function BranchCountsField({ value, levels, onChange }: { value: unknown; levels: number; onChange: (v: unknown) => void }) {
  const mode = Array.isArray(value) ? 'perLevel' : typeof value === 'number' ? 'all' : '';
  return (
    <>
      <Enum
        label="Branches"
        hint="Children per parent: one number for every level, or one per level [level1, level2, ...]."
        value={mode || undefined}
        unset="Preset"
        options={[
          { value: 'all', label: 'Same on every level' },
          { value: 'perLevel', label: 'Per level' },
        ]}
        onChange={(m) => {
          if (m === 'all') onChange(Array.isArray(value) ? (value[0] ?? 5) : 5);
          else if (m === 'perLevel') onChange(Array.from({ length: Math.max(1, levels) }, (_, i) => (typeof value === 'number' ? value : [6, 4, 3, 2][i] ?? 2)));
          else onChange(undefined);
        }}
      />
      {mode === 'all' && <Num label="Count" hint="Children per parent on every level." integer min={0} value={num(value)} onChange={onChange} />}
      {mode === 'perLevel' && (
        <IntList label="Per level" hint="Children per parent on level 1, 2, ..." labels={(i) => `L${i + 1}`} value={arr<number>(value)} onChange={(v) => onChange(v && v.length ? v : undefined)} />
      )}
    </>
  );
}

/** Branch levels carrying leaves (0 = trunk) as toggles; unset = preset. */
function LevelsField({ value, onChange }: { value: number[] | undefined; onChange: (v: number[] | undefined) => void }) {
  return (
    <PropRow label="Leaf levels" hint="Branch levels that carry leaves (0 = trunk). None selected = the preset's.">
      <div className="level-toggles">
        {[0, 1, 2, 3, 4].map((l) => {
          const on = value?.includes(l) ?? false;
          return (
            <button
              key={l}
              type="button"
              className={on ? 'is-active' : ''}
              aria-pressed={on}
              title={l === 0 ? 'Trunk' : `Level ${l}`}
              onClick={() => {
                const next = on ? (value ?? []).filter((x) => x !== l) : [...(value ?? []), l].sort((a, b) => a - b);
                onChange(next.length ? next : undefined);
              }}
            >
              {l}
            </button>
          );
        })}
      </div>
      {value && <IconButton icon="x" title="Reset (use the preset)" size={12} className="field-reset" onClick={() => onChange(undefined)} />}
    </PropRow>
  );
}

function FruitFields({ value, set, materials, petals }: { value: Obj; set: (k: string, v: unknown) => void; materials: string[]; petals: boolean }) {
  return (
    <>
      <Num label="Chance" hint="Chance per twig tip 0..1." optional min={0} max={1} step={0.05} value={num(value.chance)} onChange={(v) => set('chance', v)} />
      <Num label="Size" hint="Diameter (m)." optional min={0} value={num(value.size)} onChange={(v) => set('size', v)} />
      <Color label="Color" hint="sRGB vertex color." value={vec(value.color, 3)} onChange={(v) => set('color', v)} />
      <Pick label="Material" hint="Material for these (unset = the part's material)." none="Part material" options={materials} value={str(value.material)} onChange={(v) => set('material', v)} />
      {petals && <Num label="Petals" hint="Petals per flower." optional integer min={0} value={num(value.petals)} onChange={(v) => set('petals', v)} />}
    </>
  );
}

/** `tree`: SpeedTree-style procedural plant; every unset field comes from `preset`. */
export function TreeEditor({ shape, onChange, doc }: ShapeEditorProps) {
  const set = (key: string, v: unknown) => onChange(patch(shape, key, v));
  const n = (key: string, label: string, hint: string, extra: { integer?: boolean; min?: number; max?: number; step?: number } = {}) => (
    <Num label={label} hint={hint} optional value={num(shape[key])} onChange={(v) => set(key, v)} {...extra} />
  );
  const materials = materialNames(doc);
  const leaves = obj(shape.leaves);
  return (
    <>
      <Section title="Plant">
        <Enum label="Preset" hint={PRESET_HINT} value={str(shape.preset)} unset="Oak (default)" options={TREE_PRESETS} onChange={(v) => set('preset', v)} />
        <Num label="Seed" hint="A different but equally plausible individual (same seed = identical mesh)." integer min={0} value={num(shape.seed)} fallback={0} onChange={(v) => set('seed', v)} />
        {n('height', 'Height', 'Plant height (m); the base sits at y = 0.', { min: 0, step: 0.1 })}
        {n('fullness', 'Fullness', 'Crown fullness: multiplies the leaf density (0.3 sparse .. 1 default .. 2 lush).', { min: 0, step: 0.05 })}
      </Section>
      <Section title="Trunk">
        {n('trunkRadius', 'Radius', 'Trunk radius at the base (m); 0 = no trunk wood (grass, ferns).', { min: 0 })}
        {n('trunkTaper', 'Taper', 'How much the trunk thins toward the top (0..1).', { min: 0, max: 1, step: 0.05 })}
        {n('trunkSplit', 'Split', 'Fraction of the height where the trunk forks (0 = no fork).', { min: 0, max: 1, step: 0.05 })}
        {n('splitCount', 'Split count', 'Stems the trunk forks into (2-6).', { integer: true, min: 2, max: 6 })}
        {n('roots', 'Roots', 'Root flare and buttress lobes at the base (0..1+).', { min: 0, step: 0.05 })}
        {n('segments', 'Segments', 'Radial segments of the trunk (thinner branches use fewer).', { integer: true, min: 3 })}
        {n('lengthSegments', 'Length segments', 'Segments along the trunk (shorter branches use fewer).', { integer: true, min: 1 })}
      </Section>
      <Section title="Branches">
        {n('levels', 'Levels', 'Branch recursion depth 1-4 (trunk → branches → twigs …).', { integer: true, min: 1, max: 4 })}
        <BranchCountsField value={shape.branches} levels={num(shape.levels) ?? 3} onChange={(v) => set('branches', v)} />
        {n('branchStart', 'Start', 'Fraction of the trunk below the first branch (0..0.99).', { min: 0, max: 0.99, step: 0.05 })}
        {n('branchAngle', 'Angle', 'Degrees between parent and child.', { step: 1 })}
        {n('branchAngleVariance', 'Angle variance', '± random variation of the branch angle (degrees).', { min: 0, step: 1 })}
        {n('lengthRatio', 'Length ratio', 'Child length / parent length (first-order branches: / height).', { min: 0, step: 0.05 })}
        {n('radiusRatio', 'Radius ratio', 'Child radius / parent radius at the attachment.', { min: 0, step: 0.05 })}
        {n('gravity', 'Gravity', 'Droop: positive bends branches down (weeping), negative up (firs).', { step: 0.05 })}
        {n('gnarl', 'Gnarl', 'Random kinks along branches (0 straight .. 1 very gnarled).', { min: 0, step: 0.05 })}
        {n('twist', 'Twist', 'Degrees between successive children around their parent (137.5 = spiral, 180 alternate, 90 opposite pairs).', { step: 1 })}
        {n('curvature', 'Curvature', 'Degrees each branch curves upward along its length (negative = down).', { step: 1 })}
        <Enum
          label="Crown shape"
          hint="Envelope of the first-order branch lengths along the trunk: conical (spruce), spherical (oak), columnar (poplar), umbrella (palm, acacia), weeping (willow)."
          value={str(shape.crownShape)}
          unset="Preset"
          options={CROWN_SHAPES}
          onChange={(v) => set('crownShape', v)}
        />
      </Section>
      <Section title="Leaves">
        <Pick label="Leaf material" hint="Material of the leaves (unset = the part's material, which is the wood)." none="Part material" options={materials} value={str(shape.leafMaterial)} onChange={(v) => set('leafMaterial', v)} />
        <OptionalSection title="Override leaves" hint="Leaves of the plant; unset fields come from the preset." value={leaves} make={(): Obj => ({})} onChange={(v) => set('leaves', v)}>
          {(l, setL) => (
            <>
              <Enum
                label="Type"
                hint="broad: leaf cards · needle: crossed tufts · frond / blade: one ribbon per leaf-bearing stem · petal · cluster: large twig cards (dense crowns) · none."
                value={str(l.type)}
                unset="Preset"
                options={LEAF_TYPES}
                onChange={(v) => setL('type', v)}
              />
              <Num label="Size" hint="Leaf card length (m); ribbon width for frond / blade." optional min={0} value={num(l.size)} onChange={(v) => setL('size', v)} />
              <Num label="Density" hint="Leaf attachment points per meter of leaf-bearing branch." optional min={0} step={0.5} value={num(l.density)} onChange={(v) => setL('density', v)} />
              <Num label="Cluster size" hint="Leaves fanned out at each attachment point." optional integer min={1} value={num(l.clusterSize)} onChange={(v) => setL('clusterSize', v)} />
              <LevelsField value={Array.isArray(l.levels) ? (l.levels as number[]) : undefined} onChange={(v) => setL('levels', v)} />
              <Num label="Angle" hint="Degrees between the twig and the leaf." optional step={1} value={num(l.angle)} onChange={(v) => setL('angle', v)} />
              <Num label="Bend" hint="Degrees the leaf curls down along its length." optional step={1} value={num(l.bend)} onChange={(v) => setL('bend', v)} />
              <Num label="Aspect" hint="Card width / length (1 = square, matching the leaf textures)." optional min={0} step={0.05} value={num(l.aspect)} onChange={(v) => setL('aspect', v)} />
              <Color label="Tint" hint="sRGB tint written into vertex colors (multiplies the material)." value={vec(l.color, 3)} onChange={(v) => setL('color', v)} />
              <Num label="Color variation" hint="Per-leaf hue / brightness variation 0..1." optional min={0} max={1} step={0.05} value={num(l.colorVariation)} onChange={(v) => setL('colorVariation', v)} />
              <Bool label="Cards" hint="Alpha-cut cards (on) or solid leaf-shaped polygons (off, low-poly styles)." optional value={bool(l.cards)} onChange={(v) => setL('cards', v)} />
            </>
          )}
        </OptionalSection>
      </Section>
      <Section title="Flowers & fruit" open={Boolean(shape.flowers || shape.fruit)}>
        <OptionalSection title="Flowers" hint="Flowers at branch tips." value={obj(shape.flowers)} make={() => ({ chance: 0.3, size: 0.05 })} onChange={(v) => set('flowers', v)}>
          {(f, setF) => <FruitFields value={f} set={setF} materials={materials} petals />}
        </OptionalSection>
        <OptionalSection title="Fruit" hint="Fruit at branch tips." value={obj(shape.fruit)} make={() => ({ chance: 0.2, size: 0.08 })} onChange={(v) => set('fruit', v)}>
          {(f, setF) => <FruitFields value={f} set={setF} materials={materials} petals={false} />}
        </OptionalSection>
      </Section>
    </>
  );
}
